import React, { useState, useEffect } from 'react'
import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Tag,
  Tooltip,
  Upload,
  Popover,
  Radio,
  Table,
  Space,
  Divider,
  Row,
  Col,
  Checkbox,
  Collapse,
  Typography,
  InputNumber,
  message,
  Spin
} from 'antd'
import {
  PlusOutlined,
  UploadOutlined,
  StarOutlined,
  StarFilled,
  InfoCircleOutlined,
  CameraOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import './compact-dialog-styles.css'
import { ApiRequest } from '../../../services/api/api'
import { getCookie } from 'react-use-cookie'
import { imgStringToArray, imgArrayToString } from '../../../components/common/common-column-components/common-edit-image-component'
import { baseUrl } from '../../../services/api/base-url'
import { getAuthHeaders } from '../../../services/lib/utils/auth.utils'
import { getSelectOptions } from '../../../components/common/common-form/common-form-select'
import { urlKey } from '../../../services/api/api-urls'
import PackagingTypeCascadeSelect from '../../../components/common/packaging-type-cascade-select'
import moment from 'moment'

const { Option } = Select
const { Panel } = Collapse
const { Text } = Typography

// Spec fields stored on the BaseProduct (shared by initial load & base-product reassignment)
const BASE_PRODUCT_SPEC_FIELDS = [
  'brimFillCapacityMl', 'dimsTopLMm', 'dimsTopWMm', 'dimsBaseLMm', 'dimsBaseWMm',
  'productHeightMm', 'productWeightG', 'wallThicknessMm', 'rawMaterialText',
  'manufacturedLocation', 'printingType', 'liningCoating', 'facilityQualitySystem',
  'cartonLength', 'cartonWidth', 'cartonHeight', 'grossWeigh', 'packagingTypeId'
]

// Critical inline styles for immediate compact effect
const compactStyles = `
  .compact-product-dialog .ant-form-item { margin-bottom: 8px !important; }
  .compact-product-dialog .ant-form-item-label > label { font-size: 13px !important; height: 26px !important; }
  .compact-product-dialog .ant-input, .compact-product-dialog .ant-input-number, .compact-product-dialog .ant-select-selector { font-size: 14px !important; }
  .compact-product-dialog .ant-table { font-size: 14px !important; }
  .compact-product-dialog .ant-table-thead > tr > th { padding: 8px 10px !important; font-size: 13px !important; }
  .compact-product-dialog .ant-table-tbody > tr > td { padding: 8px 10px !important; font-size: 14px !important; }
  .compact-product-dialog .ant-btn { font-size: 13px !important; height: 30px !important; padding: 4px 10px !important; }
  .compact-product-dialog .ant-divider { margin: 8px 0 !important; }
  /* Specifications tab: extra vertical compactness to avoid scrolling */
  .compact-spec .ant-divider { margin: 2px 0 4px 0 !important; }
  .compact-spec .ant-form-item { margin-bottom: 2px !important; }
  .compact-spec .ant-form-item-label > label { height: 18px !important; }
  .compact-spec .ant-alert { padding: 2px 8px !important; margin-bottom: 4px !important; font-size: 13px !important; }
  .compact-spec .ant-collapse > .ant-collapse-item { margin-bottom: 2px !important; border-radius: 4px !important; }
  .compact-spec .ant-collapse-header { padding: 4px 8px !important; font-size: 13px !important; min-height: 0 !important; line-height: 20px !important; }
  .compact-spec .ant-collapse-content-box { padding: 4px 8px !important; }
  .compact-spec .ant-table-thead > tr > th { padding: 4px 6px !important; }
  .compact-spec .ant-table-tbody > tr > td { padding: 4px 6px !important; }
  /* Specifications tab: read-only display (inherited from Base Product) */
  .spec-readonly .ant-form-item-label > label { color: #8c8c8c !important; }
  .spec-readonly .ant-input { background-color: #f5f5f5 !important; color: #8c8c8c !important; border-color: #e8e8e8 !important; cursor: default !important; }
  .spec-readonly .ant-select-selector { background-color: #f5f5f5 !important; color: #8c8c8c !important; border-color: #e8e8e8 !important; }
  .spec-readonly .ant-select-arrow { color: #bfbfbf !important; }
  .spec-readonly .ant-input-number { background-color: #f5f5f5 !important; border-color: #e8e8e8 !important; }
  .spec-readonly .ant-input-number-input { color: #8c8c8c !important; cursor: default !important; }
  .spec-readonly .ant-input-number-handler-wrap { display: none !important; }
  .spec-readonly .ant-checkbox-disabled + span { color: #8c8c8c !important; }
  .spec-readonly .ant-collapse { background: #fafafa !important; }
  .spec-readonly .ant-collapse-header { color: #595959 !important; }
  .spec-readonly .ant-collapse-content-box { color: #595959 !important; }
  .spec-readonly .ant-table { background: #fafafa !important; color: #595959 !important; }
  .spec-readonly .ant-alert { background: #f5f5f5 !important; }
  .spec-readonly .ant-divider-inner-text { color: #595959 !important; }
  /* 1:1 base product: classification & specification details stay editable */
  .spec-editable .ant-form-item-label > label { color: rgba(0, 0, 0, 0.85) !important; }
  .spec-editable .ant-input { background-color: #fff !important; color: rgba(0, 0, 0, 0.85) !important; border-color: #d9d9d9 !important; cursor: text !important; }
  .spec-editable .ant-select-selector { background-color: #fff !important; color: rgba(0, 0, 0, 0.85) !important; border-color: #d9d9d9 !important; }
  .spec-editable .ant-select-arrow { color: #999 !important; }
  .spec-editable .ant-checkbox-disabled + span { color: rgba(0, 0, 0, 0.85) !important; }
  .spec-editable .ant-divider-inner-text { color: rgba(0, 0, 0, 0.85) !important; }
`

// Inject critical styles
if (typeof document !== 'undefined' && !document.getElementById('compact-dialog-styles')) {
  const style = document.createElement('style')
  style.id = 'compact-dialog-styles'
  style.textContent = compactStyles
  document.head.appendChild(style)
}

interface ProductManagementDialogProps {
  visible: boolean
  onClose: (saved?: boolean) => void
  productData?: any
  mode?: 'add' | 'edit' | 'view'
}

export const ProductManagementDialog: React.FC<ProductManagementDialogProps> = ({
  visible,
  onClose,
  productData,
  mode = 'edit'
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('general')
  const [customerProductCodeEnabled, setCustomerProductCodeEnabled] = useState(false)
  const [customerOptions, setCustomerOptions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  // 初始下拉选项（base product / pallet type / pallet stacking 等）是否加载完成：
  // 未加载完成前隐藏底部按钮，防止提前保存
  const [dataLoading, setDataLoading] = useState(true)
  // Packaging Unit 级联组件的包装类型数据加载中
  const [cascadeLoading, setCascadeLoading] = useState(false)
  const [productDetails, setProductDetails] = useState<any>(null)
  const [stockInfo, setStockInfo] = useState<any>(null)
  const [workOrderData, setWorkOrderData] = useState<any[]>([])
  const [purchaseOrderData, setPurchaseOrderData] = useState<any[]>([])
  const [basePricingData, setBasePricingData] = useState<any[]>([])
  const [customerPricingData, setCustomerPricingData] = useState<any[]>([])
  // How many products reference the base product (specs editable only for 1:1 mapping)
  const [baseProductProductCount, setBaseProductProductCount] = useState<number | undefined>(undefined)
  const [productRoutings, setProductRoutings] = useState<any[]>([])
  const [routingActions, setRoutingActions] = useState<any>({})
  const [routingActionsLoading, setRoutingActionsLoading] = useState<any>({})
  const [expandedRoutingKeys, setExpandedRoutingKeys] = useState<string[]>([])
  const [machineLookup, setMachineLookup] = useState<any>({})
  const [classificationOptions, setClassificationOptions] = useState<any[]>([])
  const [classificationPrimaryId, setClassificationPrimaryId] = useState<number | undefined>(undefined)
  const [classificationChildId, setClassificationChildId] = useState<number | undefined>(undefined)
  const [classificationGrandchildId, setClassificationGrandchildId] = useState<number | undefined>(undefined)
  // Product-level lookups: base product / pallet type / pallet stacking (editable & saved on the Product)
  const [baseProductOptions, setBaseProductOptions] = useState<any[]>([])
  const [plateTypeOptions, setPlateTypeOptions] = useState<any[]>([])
  const [palletStackingOptions, setPalletStackingOptions] = useState<any[]>([])
  // Upload file lists are kept as objects (NOT re-parsed from a string on every render):
  // a new fileList array each render resets antd Upload's internal list and silently
  // discards in-flight uploads when any async data lands mid-upload.
  const [productFileList, setProductFileList] = useState<any[]>([])
  const [logoFileList, setLogoFileList] = useState<any[]>([])
  const [customerImageFileList, setCustomerImageFileList] = useState<any[]>([])
  const [logoType, setLogoType] = useState<number>(0)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  // Same lookup as product-management-column-model
  const logoTypeOptions = [
    { value: 0, label: 'Normal' },
    { value: 1, label: 'With Customer Logo' },
    { value: 2, label: 'Without Jadcup Logo' },
    { value: 3, label: 'Without made in NZ' },
    { value: 4, label: 'Tip Top Label' },
  ]

  // Load product data when dialog opens in edit/view mode
  useEffect(() => {
    const loadProductData = async () => {
      if (!visible) return

      if ((mode === 'edit' || mode === 'view') && productData?.productId) {
        setLoading(true)
        try {
          const response = await ApiRequest({
            url: `Product/GetProductById?id=${productData.productId}`,
            method: 'get',
            isShowSpinner: false
          })

          if (response.data && response.data.data) {
            const product = response.data.data
            setProductDetails(product)

            // Load images, customer images & logo (same format as common-edit-image-component)
            setProductFileList(imgStringToArray(product.images || ''))
            setLogoFileList(imgStringToArray(product.logoUrl || ''))
            // 后端字段为 showImgUrl
            setCustomerImageFileList(imgStringToArray(product.showImgUrl || ''))
            setLogoType(product.logoType ?? 0)

            // Transform API data to form format
            const formValues: any = {
              // Basic Info
              productCode: product.productCode || '',
              productName: product.productName || '',
              productMsl: product.productMsl ?? undefined,
              minOrderQuantity: product.minOrderQuantity ?? undefined,

              // Pricing
              customerType: product.customerId ? 'specific' : 'general',
              customerId: product.customerId ?? null,

              // Product-level packaging fields (editable & saved on the Product)
              baseProductId: product.baseProductId ?? product.baseProduct?.baseProductId ?? undefined,
              plateTypeId: product.plateTypeId ?? product.plateType?.plateTypeId ?? undefined,
              // 后端无数据时可能返回 0：antd Select 找不到 value=0 的选项会把 "0" 原样显示出来，
              // 这里用 || 把 0/undefined/null 都归一化为 undefined（下拉显示空白）
              palletStackingId: product.palletStackingId || product.palletStacking?.palletStackingId || undefined,
              plain: product.plain != null ? Number(product.plain) : 0,
            }

            // Specification details live on the base product (GetBaseProductDto fields)
            const baseProduct = product.baseProduct || {}
            BASE_PRODUCT_SPEC_FIELDS.forEach(field => {
              if (baseProduct[field] !== undefined && baseProduct[field] !== null) {
                formValues[field] = baseProduct[field]
              }
            })
            formValues.classificationId = baseProduct.classificationId ?? undefined
            formValues.isHomeCompostable = Number(baseProduct.isHomeCompostable) === 1
            formValues.isIndustrialCompostable = Number(baseProduct.isIndustrialCompostable) === 1
            formValues.isRecyclable = Number(baseProduct.isRecyclable) === 1

            // Cust Product Code: same switch logic as the Products table
            // (switch on only when a different code is stored; "Same" mirrors Product Code)
            const useDifferentCustCode = !!(product.custProductCode && product.custProductCode !== product.productCode)
            setCustomerProductCodeEnabled(useDifferentCustCode)
            formValues.customerProductCode = useDifferentCustCode
              ? product.custProductCode
              : (product.productCode || '')

            form.setFieldsValue(formValues)

            // Fetch stock & pricing data (same APIs as i-tips / base product pages)
            fetchStockData(product.productId)
            const baseProductId = product.baseProductId ?? product.baseProduct?.baseProductId
            if (baseProductId) {
              fetchProductRoutings(baseProductId)
              ApiRequest({
                url: `Product/GetProductByBaseProduct?id=${baseProductId}`,
                method: 'get',
                isShowSpinner: false
              }).then((res: any) => {
                setBaseProductProductCount(res.data?.data?.length ?? 0)
              }).catch((error: any) => {
                console.error('Failed to load products for base product:', error)
                setBaseProductProductCount(undefined)
              })
            }
            await fetchPricingData(product)
          }
        } catch (error) {
          console.error('Failed to load product data:', error)
          message.error('Failed to load product details')
          // Fallback to productData if API fails
          if (productData) {
            form.setFieldsValue(productData)
          }
        } finally {
          setLoading(false)
        }
      } else if (mode === 'add') {
        // Reset form for add mode
        form.resetFields()
        setCustomerProductCodeEnabled(false)
        form.setFieldsValue({ customerType: 'general', customerId: null, customerProductCode: '' })
        setProductDetails(null)
        setStockInfo(null)
        setWorkOrderData([])
        setPurchaseOrderData([])
        setBasePricingData([])
        setCustomerPricingData([])
        setProductRoutings([])
        setBaseProductProductCount(undefined)
        setRoutingActions({})
        setExpandedRoutingKeys([])
        setProductFileList([])
        setLogoFileList([])
        setCustomerImageFileList([])
        setLogoType(0)
      }
    }

    loadProductData()
  }, [visible, mode, productData])

  // Customer options for Customer Scope (same endpoint as box/pallet pages)
  useEffect(() => {
    if (!visible) return
    ApiRequest({
      url: 'Customer/GetAllSimpleCustomer',
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      setCustomerOptions(res.data?.data || [])
    }).catch(error => {
      console.error('Failed to fetch customer options:', error)
      setCustomerOptions([])
    })
  }, [visible])

  // Product classification options (ProductClassification/GetAll)
  useEffect(() => {
    if (!visible) return
    ApiRequest({
      url: 'ProductClassification/GetAll?includeDeleted=false',
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      setClassificationOptions(res.data?.data || [])
    }).catch(error => {
      console.error('Failed to fetch product classifications:', error)
      setClassificationOptions([])
    })
  }, [visible])

  // Base product / pallet type / pallet stacking options (Product-level editable fields)
  useEffect(() => {
    if (!visible) return
    // 必填项（Base Product / Pallet Stacking）依赖这些选项，全部加载完成后才允许保存
    setDataLoading(true)
    Promise.all([
      getSelectOptions(urlKey.BaseProduct),
      getSelectOptions(urlKey.PlateType),
      getSelectOptions(urlKey.PalletStacking),
    ]).then(([baseProducts, plateTypes, palletStackings]: any[]) => {
      setBaseProductOptions(baseProducts || [])
      const pt = plateTypes || []
      setPlateTypeOptions(pt)
      setPalletStackingOptions(palletStackings || [])
    }).catch((error: any) => {
      console.error('Failed to fetch product options:', error)
      setBaseProductOptions([])
      setPlateTypeOptions([])
      setPalletStackingOptions([])
    }).finally(() => {
      setDataLoading(false)
    })
  }, [visible])

  // Pallet Type 必填且默认“普通托盘”：选项与产品详情都就绪后，字段仍为空则自动选中
  // 名称含 普通/Normal 的托盘（历史数据未填写时也生效）
  useEffect(() => {
    if (!visible || !plateTypeOptions.length) return
    if (form.getFieldValue('plateTypeId')) return
    const normalPallet = plateTypeOptions.find((row: any) => /普通|normal/i.test(row.plateTypeName || ''))
    if (normalPallet) {
      form.setFieldsValue({ plateTypeId: normalPallet.plateTypeId })
    }
  }, [visible, plateTypeOptions, productDetails, form])

  // Derive the classification cascade path (primary -> child -> grandchild) from the base product
  useEffect(() => {
    const classificationId = productDetails?.baseProduct?.classificationId
    if (!classificationId || !classificationOptions.length) return
    const map: any = {}
    classificationOptions.forEach((classification: any) => {
      map[classification.classificationId] = classification
    })
    const path: any[] = []
    let current = map[classificationId]
    while (current) {
      path.unshift(current)
      current = current.parentId ? map[current.parentId] : null
    }
    setClassificationPrimaryId(path[0]?.classificationId)
    setClassificationChildId(path[1]?.classificationId)
    setClassificationGrandchildId(path[2]?.classificationId)
  }, [productDetails, classificationOptions])

  const classificationChildrenOf = (parentId: number | null | undefined) =>
    classificationOptions.filter((classification: any) =>
      parentId == null ? !classification.parentId : classification.parentId === parentId
    )

  const handleClassificationSelect = (level: 'primary' | 'child' | 'grandchild', id?: number) => {
    let finalId = id ?? null
    if (level === 'primary') {
      setClassificationPrimaryId(id)
      setClassificationChildId(undefined)
      setClassificationGrandchildId(undefined)
    } else if (level === 'child') {
      setClassificationChildId(id)
      setClassificationGrandchildId(undefined)
      finalId = id ?? classificationPrimaryId ?? null
    } else {
      setClassificationGrandchildId(id)
      finalId = id ?? classificationChildId ?? null
    }
    form.setFieldsValue({ classificationId: finalId })
  }

  // Reassigning the base product: refresh inherited specs / routings / pricing
  // from the newly selected base product (nothing is persisted until Save)
  const handleBaseProductChange = async (baseProductId?: number) => {
    if (!baseProductId || mode === 'view') return
    try {
      const response = await ApiRequest({
        url: `BaseProduct/GetBaseProductById?id=${baseProductId}&inclInactive=1`,
        method: 'get',
        isShowSpinner: false
      })
      const baseProduct = response.data?.data
      if (!baseProduct) return

      setProductDetails((prev: any) => ({ ...(prev || {}), baseProductId, baseProduct }))

      const specFieldValues: any = {}
      BASE_PRODUCT_SPEC_FIELDS.forEach(field => {
        specFieldValues[field] = baseProduct[field] ?? undefined
      })
      specFieldValues.classificationId = baseProduct.classificationId ?? undefined
      specFieldValues.isHomeCompostable = Number(baseProduct.isHomeCompostable) === 1
      specFieldValues.isIndustrialCompostable = Number(baseProduct.isIndustrialCompostable) === 1
      specFieldValues.isRecyclable = Number(baseProduct.isRecyclable) === 1
      form.setFieldsValue(specFieldValues)

      // Refresh base-product-driven sections for the new selection
      fetchProductRoutings(baseProductId)
      fetchBaseProductPricing(baseProductId).then((pricing: any) => setBasePricingData(pricing || []))
      ApiRequest({
        url: `Product/GetProductByBaseProduct?id=${baseProductId}`,
        method: 'get',
        isShowSpinner: false
      }).then((res: any) => {
        setBaseProductProductCount(res.data?.data?.length ?? 0)
      }).catch((error: any) => {
        console.error('Failed to load products for base product:', error)
        setBaseProductProductCount(undefined)
      })
    } catch (error) {
      console.error('Failed to load selected base product:', error)
      message.error('Failed to load selected base product details')
    }
  }

  // Fetch pricing data for edit/view modes
  const fetchPricingData = async (product: any) => {
    try {
      const baseProductId = product.baseProductId ?? product.baseProduct?.baseProductId
      const [basePricing, customerPricing] = await Promise.all([
        baseProductId ? fetchBaseProductPricing(baseProductId) : Promise.resolve([]),
        fetchCustomerPricing(product.productId)
      ])

      setBasePricingData(basePricing || [])
      setCustomerPricingData(customerPricing || [])
    } catch (error) {
      console.error('Failed to fetch pricing data:', error)
    }
  }

  // Global compact styling
  const compactFormStyle = {
    fontSize: '15px',
    '.ant-form-item': { marginBottom: '8px' },
    '.ant-form-item-label > label': { fontSize: '13px', height: '24px' },
    '.ant-input, .ant-input-number, .ant-select-selector': { fontSize: '13px' }
  }

  useEffect(() => {
    if (visible && productData) {
      form.setFieldsValue(productData)
    }
  }, [visible, productData, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      console.log('Form values:', values)

      // Packaging Unit 仅在 1:1 产品编辑时可改（specEditable），保存时必填；
      // Form.Item 为 noStyle 不会展示校验错误，这里手动校验并给出明确提示
      if (specEditable && !values.packagingTypeId) {
        message.error('Please select Packaging Unit (Carton Size / Qty per Sleeve).')
        return
      }

      setLoading(true)
      let response

      // Same as the table's mappingUpdateData: derive from the actual code values
      const codesAreDifferent = !!(
        values.customerProductCode
        && values.productCode
        && values.customerProductCode !== values.productCode
      )

      if (mode === 'add') {
        response = await ApiRequest({
          url: 'Product/AddProduct',
          method: 'post',
          data: {
            ...values,
            plain: values.plain ?? 0,
            // Pallet Stacking 未选择时显式传 null（undefined 会被 JSON 序列化丢弃，字段就传不过去了）
            palletStackingId: values.palletStackingId ?? null,
            // Default Margin of Error 不在界面显示，默认 100 保存
            marginOfError: 100,
            custProductCode: values.customerProductCode ?? '',
            images: fileListToImageString(productFileList),
            logoUrl: fileListToImageString(logoFileList),
            showImgUrl: fileListToImageString(customerImageFileList),
            logoType,
            employeeId: getCookie('customerUserId') ? 0 : parseInt(getCookie('id')),
            useDifferentCustCode: codesAreDifferent ? 1 : 0
          },
          isShowSpinner: false
        })
        message.success('Product added successfully')
      } else {
        response = await ApiRequest({
          url: 'Product/UpdateProduct',
          method: 'put',
          data: {
            ...values,
            productId: productData?.productId,
            plain: values.plain ?? (productDetails?.plain != null ? Number(productDetails.plain) : 0),
            // Pallet Stacking 未选择/被清空时显式传 null（undefined 会被 JSON 序列化丢弃，字段就传不过去了）
            palletStackingId: values.palletStackingId ?? null,
            // Default Margin of Error 不在界面显示：保留原值，无原值时默认 100
            marginOfError: Number(productDetails?.marginOfError) || 100,
            custProductCode: values.customerProductCode ?? '',
            images: fileListToImageString(productFileList),
            logoUrl: fileListToImageString(logoFileList),
            showImgUrl: fileListToImageString(customerImageFileList),
            logoType,
            employeeId: getCookie('customerUserId') ? 0 : parseInt(getCookie('id')),
            useDifferentCustCode: codesAreDifferent ? 1 : 0
          },
          isShowSpinner: false
        })

        // 1:1 base product: persist editable spec fields & classification to the BaseProduct
        // (Product/UpdateProduct only maps Product entity fields).
        // The details must match the currently selected base product: if it was just
        // reassigned and its details failed to load, skip the write instead of
        // saving specs onto the previous base product.
        const targetBaseProductId = values.baseProductId ?? productDetails?.baseProductId ?? productDetails?.baseProduct?.baseProductId
        const baseProductDetailsInSync = !!targetBaseProductId
          && productDetails?.baseProduct?.baseProductId === targetBaseProductId
        if (specEditable && baseProductDetailsInSync) {
          const toNumberOrNull = (v: any) =>
            (v === '' || v == null || isNaN(Number(v))) ? null : Number(v)
          const baseProduct = productDetails?.baseProduct || {}
          await ApiRequest({
            url: 'BaseProduct/UpdateBaseProduct',
            method: 'put',
            data: {
              ...baseProduct,
              brimFillCapacityMl: toNumberOrNull(values.brimFillCapacityMl),
              dimsTopLMm: toNumberOrNull(values.dimsTopLMm),
              dimsTopWMm: toNumberOrNull(values.dimsTopWMm),
              dimsBaseLMm: toNumberOrNull(values.dimsBaseLMm),
              dimsBaseWMm: toNumberOrNull(values.dimsBaseWMm),
              productHeightMm: toNumberOrNull(values.productHeightMm),
              productWeightG: toNumberOrNull(values.productWeightG),
              wallThicknessMm: toNumberOrNull(values.wallThicknessMm),
              cartonLength: toNumberOrNull(values.cartonLength),
              cartonWidth: toNumberOrNull(values.cartonWidth),
              cartonHeight: toNumberOrNull(values.cartonHeight),
              // 后端 grossWeigh 是 string 类型，空值传 null
              grossWeigh: (values.grossWeigh === '' || values.grossWeigh == null) ? null : String(values.grossWeigh),
              packagingTypeId: values.packagingTypeId ?? null,
              rawMaterialText: values.rawMaterialText ?? null,
              manufacturedLocation: values.manufacturedLocation ?? null,
              printingType: values.printingType ?? null,
              liningCoating: values.liningCoating ?? null,
              facilityQualitySystem: values.facilityQualitySystem ?? null,
              isHomeCompostable: values.isHomeCompostable ? 1 : 0,
              isIndustrialCompostable: values.isIndustrialCompostable ? 1 : 0,
              isRecyclable: values.isRecyclable ? 1 : 0,
              classificationId: values.classificationId ?? null
            },
            isShowSpinner: false
          })
        }

        message.success('Product updated successfully')
      }

      onClose(true)
    } catch (error: any) {
      console.error('Save failed:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save product'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const handleCustomerProductCodeToggle = (checked: boolean) => {
    setCustomerProductCodeEnabled(checked)
    if (!checked) {
      // Same as table: switching back to "Same" syncs the code to Product Code
      form.setFieldsValue({ customerProductCode: form.getFieldValue('productCode') || '' })
    }
  }

  // Real upload config (same endpoint & storage format as common-edit-image-component)
  const handleImagePreview = async (file: UploadFile, e?: { preventDefault: () => void }) => {
    e?.preventDefault()
    let src = file.url || (file.preview as string)
    if (!src && file.originFileObj) {
      src = await new Promise(resolve => {
        const reader = new FileReader()
        reader.readAsDataURL(file.originFileObj as Blob)
        reader.onload = () => resolve(reader.result as string)
      })
      file.preview = src
    }
    if (src) {
      setPreviewImage(src)
      setPreviewVisible(true)
    }
  }

  // fileList comes from state (stable reference, includes in-flight files);
  // onChange mirrors antd's list on every event (uploading/done/error/removed)
  const buildUploadProps = (setter: (list: any[]) => void, current: any[]): UploadProps => ({
    name: 'imageFile',
    action: baseUrl + 'Common/UploadImage',
    // antd Upload 用自带 XHR 发请求，不走 axios 拦截器，必须手动带 token，否则 401
    headers: getAuthHeaders(),
    listType: 'picture-card',
    multiple: true,
    onPreview: handleImagePreview,
    fileList: current,
    beforeUpload: (file) => {
      const MAX_FILE_SIZE = 25 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        message.error(`File too large! Max 25MB, current ${(file.size / 1024 / 1024).toFixed(2)}MB`)
        return false
      }
      return true
    },
    onChange: (info: any) => {
      setter(info.fileList)
    }
  })

  // Storage format: {"uid":1,"name":"a.jpg","url":"..."} joined by '---'
  // (only files that actually have a URL are persisted; failed uploads are skipped)
  const fileListToImageString = (fileList: any[]) =>
    imgArrayToString(
      (fileList || [])
        .filter((row: any) => row.url || row.response)
        .map((row: any, index: number) => ({
          uid: index + 1,
          name: row.name,
          url: row.url || row.response
        }))
    )

  // API calls for additional data (same endpoints as i-tips/product component)
  const fetchStockData = (productId: number) => {
    ApiRequest({
      url: `StockMonitor/GetStockInfoByProductId?productId=${productId}`,
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      setStockInfo(res.data?.data || null)
    }).catch(error => {
      console.error('Failed to fetch stock info:', error)
    })

    ApiRequest({
      url: `WorkOrder/GetWorkOrderyProductId?productId=${productId}`,
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      setWorkOrderData(res.data?.data || [])
    }).catch(error => {
      console.error('Failed to fetch work orders:', error)
    })

    ApiRequest({
      url: `StockMonitor/GetPoQtyByProductId?productId=${productId}`,
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      setPurchaseOrderData(res.data?.data || [])
    }).catch(error => {
      console.error('Failed to fetch purchase orders:', error)
    })
  }

  // Product routings of the base product (BaseProductRouting/GetByBaseProductId)
  const fetchProductRoutings = (baseProductId: number) => {
    ApiRequest({
      url: `BaseProductRouting/GetByBaseProductId?baseProductId=${baseProductId}`,
      method: 'get',
      isShowSpinner: false
    }).then(res => {
      const routings = res.data?.data || []
      setProductRoutings(routings)

      // Expand & load machine actions of the default routing (fallback: first routing)
      const defaultRouting = routings.find((r: any) => r.isDefault) || routings[0]
      if (defaultRouting) {
        const defaultId = defaultRouting.id ?? defaultRouting.baseProductRoutingId
        setExpandedRoutingKeys([String(defaultId)])
        fetchRoutingActions(defaultId)
      }
    }).catch(error => {
      console.error('Failed to fetch product routings:', error)
      setProductRoutings([])
    })
  }

  // Machine lookup (machineId -> machine), used to resolve machine names of action routings
  const ensureMachineLookup = async () => {
    if (Object.keys(machineLookup).length > 0) return machineLookup
    try {
      const res = await ApiRequest({
        url: 'Machine/GetAllMachine',
        method: 'get',
        isShowSpinner: false
      })
      const lookup: any = {}
      ;(res.data?.data || []).forEach((machine: any) => {
        lookup[machine.machineId] = machine
      })
      setMachineLookup(lookup)
      return lookup
    } catch (error) {
      console.error('Failed to fetch machines:', error)
      return {}
    }
  }

  // Machine actions of one routing (BaseProductActionRouting/GetByBaseProductRoutingId)
  const fetchRoutingActions = async (routingId: number) => {
    setRoutingActionsLoading((prev: any) => ({ ...prev, [routingId]: true }))
    try {
      const [actionsRes] = await Promise.all([
        ApiRequest({
          url: `BaseProductActionRouting/GetByBaseProductRoutingId?baseProductRoutingId=${routingId}`,
          method: 'get',
          isShowSpinner: false
        }),
        ensureMachineLookup()
      ])
      const rows = (actionsRes.data?.data || [])
        .slice()
        .sort((a: any, b: any) => (a.sequenceNo ?? a.sequence ?? 0) - (b.sequenceNo ?? b.sequence ?? 0))
      setRoutingActions((prev: any) => ({ ...prev, [routingId]: rows }))
    } catch (error) {
      console.error('Failed to fetch routing machine actions:', error)
      setRoutingActions((prev: any) => ({ ...prev, [routingId]: [] }))
    } finally {
      setRoutingActionsLoading((prev: any) => ({ ...prev, [routingId]: false }))
    }
  }

  const handleRoutingCollapseChange = (keys: any) => {
    const activeKeys = (Array.isArray(keys) ? keys : (keys != null ? [keys] : [])) as string[]
    setExpandedRoutingKeys(activeKeys)
    activeKeys.forEach(key => {
      const routingId = Number(key)
      if (routingActions[routingId] === undefined) {
        fetchRoutingActions(routingId)
      }
    })
  }

  // Customer pricing & sales info of all customers for this product (GetCustomerPricingByProduct)
  const fetchCustomerPricing = async (productId: number) => {
    try {
      const response = await ApiRequest({
        url: `Quotation/GetCustomerPricingByProduct?productId=${productId}`,
        method: 'get',
        isShowSpinner: false
      })

      return response.data?.data || []
    } catch (error) {
      console.error('Failed to fetch customer pricing:', error)
      return []
    }
  }

  // Base product pricing (same endpoint as product-price-management-page)
  const fetchBaseProductPricing = async (baseProductId: number) => {
    try {
      const response = await ApiRequest({
        url: `ProductPrice/GetProductPriceByProduct?baseProductId=${baseProductId}`,
        method: 'get',
        isShowSpinner: false
      })

      return response.data?.data || []
    } catch (error) {
      console.error('Failed to fetch base product pricing:', error)
      return []
    }
  }

  const stockInfoContent = (
    <div style={{ maxWidth: 300 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '12px' }}>
          <Spin size="small" />
        </div>
      ) : stockInfo?.productInventoryInfo ? (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text strong>In Stock: </Text>
            <Text>
              {stockInfo.productInventoryInfo.productInStock}
              {' ('}{stockInfo.productInventoryInfo.productInStockCartons} Ctns{')'}
            </Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Approved Work Orders: </Text>
            <Text>
              {stockInfo.productInventoryInfo.pendingWorkOrderQuantity}
              {' ('}{stockInfo.productInventoryInfo.pendingWorkOrderQuantityCartons} Ctns{')'}
            </Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>In Packaging: </Text>
            <Text>
              {stockInfo.productInventoryInfo.pendingWarehouseQuantity}
              {' ('}{stockInfo.productInventoryInfo.pendingWarehouseQuantityCartons} Ctns{')'}
            </Text>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ marginBottom: 8 }}>
            <Text strong>Work Orders:</Text>
            <div style={{ marginTop: 4 }}>
              {workOrderData.length > 0 ? (
                workOrderData.map((wo: any) => {
                  const status = wo.workOrderStatus?.workOrderStatusName
                  const action = wo.actionDto?.actionName
                  const statusLabel = status === 'Progressing' || status === 'Approved' ? action : status
                  return (
                    <Tag
                      key={wo.workOrderId}
                      color={statusLabel === 'Packaging' ? 'warning' : 'success'}
                      style={{ marginBottom: 2, fontSize: '12px' }}
                    >
                      WO{wo.serialNumber}: {wo.quantity || 0}{statusLabel ? ` (${statusLabel})` : ''}
                    </Tag>
                  )
                })
              ) : (
                <Text type="secondary">No active work orders</Text>
              )}
            </div>
          </div>
          {purchaseOrderData.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Text strong>Ongoing Purchase Orders:</Text>
              <div style={{ marginTop: 4 }}>
                {purchaseOrderData.map((po: any) => (
                  <div key={po.poNo} style={{ fontSize: '13px', marginBottom: 2 }}>
                    {po.poNo}: {po.quantity} pcs{po.eta ? ` (ETA: ${po.eta})` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <Text type="secondary">No stock information available</Text>
      )}
    </div>
  )

  const basePricingColumns = [
    {
      title: 'Customer Group',
      dataIndex: 'group1',
      key: 'customerGroup',
      width: 120,
      render: (group1: any) => group1?.group1Name || 'All'
    },
    { title: 'QTY/Carton', dataIndex: 'quantiy', key: 'quantiy', width: 80 },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 60,
      render: (price: number) => price != null ? `$${Number(price).toFixed(2)}` : '-'
    },
    { title: 'MOQ', dataIndex: 'description', key: 'moqOnQuote', width: 60 }
  ]

  const customerPricingColumns = [
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 130,
      sorter: (a: any, b: any) => (a.customerName || '').localeCompare(b.customerName || ''),
      render: (customerName: string) => customerName || '-'
    },
    {
      title: 'Group',
      dataIndex: 'customerGroup',
      key: 'customerGroup',
      width: 90,
      sorter: (a: any, b: any) => (a.customerGroup || '').localeCompare(b.customerGroup || ''),
      render: (customerGroup: string) => customerGroup || '-'
    },
    {
      title: 'Contract Price',
      dataIndex: 'contractPrice',
      key: 'contractPrice',
      width: 90,
      sorter: (a: any, b: any) => (a.contractPrice ?? -Infinity) - (b.contractPrice ?? -Infinity),
      render: (price: number) => price != null ? `$${Number(price).toFixed(2)}` : '-'
    },
    {
      title: 'Markup',
      dataIndex: 'markup',
      key: 'markup',
      width: 70,
      sorter: (a: any, b: any) => (a.markup ?? -Infinity) - (b.markup ?? -Infinity),
      render: (markup: number) => markup != null ? Number(markup).toFixed(2) : '-'
    },
    {
      title: 'Sales Qty (6M)',
      dataIndex: 'salesQtyLast6Months',
      key: 'salesQtyLast6Months',
      width: 90,
      sorter: (a: any, b: any) => (a.salesQtyLast6Months ?? -Infinity) - (b.salesQtyLast6Months ?? -Infinity),
      render: (qty: number) => qty != null ? Number(qty).toLocaleString() : '-'
    },
    {
      title: 'Last Order Date',
      dataIndex: 'lastOrderDate',
      key: 'lastOrderDate',
      width: 90,
      sorter: (a: any, b: any) => moment(a.lastOrderDate || 0).valueOf() - moment(b.lastOrderDate || 0).valueOf(),
      render: (date: string) => date ? moment(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: 'Sales',
      dataIndex: 'salesName',
      key: 'salesName',
      width: 90,
      sorter: (a: any, b: any) => (a.salesName || '').localeCompare(b.salesName || ''),
      render: (salesName: string) => salesName || '-'
    }
  ]

  const machineActionColumns = [
    {
      title: '#',
      key: 'sequence',
      width: 40,
      render: (_: any, record: any) => record.sequenceNo ?? record.sequence ?? '-'
    },
    {
      title: 'Action',
      key: 'actionName',
      width: 110,
      render: (_: any, record: any) => record.actionName || record.action?.actionName || '-'
    },
    {
      title: 'Machine',
      key: 'machine',
      width: 140,
      render: (_: any, record: any) =>
        record.machineName || record.machine?.machineName || machineLookup[record.machineId]?.machineName || '-'
    }
  ]

  // Paired L x W input (e.g. top / bottom size)
  const sizePairItem = (label: string, lengthName: string, widthName: string, span = 4) => (
    <Col span={span} key={lengthName} className={specEditable ? 'spec-editable' : undefined}>
      <Form.Item label={label} style={{ marginBottom: 2 }}>
        <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
          <Form.Item name={lengthName} noStyle>
            <Input size="small" placeholder="Length" style={{ width: 'calc(50% - 6px)' }} readOnly={!specEditable} />
          </Form.Item>
          <span style={{ padding: '0 2px', fontSize: '13px', color: '#999' }}>x</span>
          <Form.Item name={widthName} noStyle>
            <Input size="small" placeholder="Width" style={{ width: 'calc(50% - 6px)' }} readOnly={!specEditable} />
          </Form.Item>
        </Input.Group>
      </Form.Item>
    </Col>
  )

  // Specs/classification live on the BaseProduct: editable only when this base product
  // maps to exactly ONE product (1:1); otherwise read-only to avoid affecting sibling products.
  // everything else in the Specifications tab stays read-only display.
  const specEditable = mode === 'edit' && baseProductProductCount === 1

  return (
    <>
    <Modal
      title={
        <div style={{ fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{mode === 'add' ? 'Add New Product' : mode === 'edit' ? 'Edit Product' : 'Product Details'}</span>
          {loading && <Spin size="small" indicator={<LoadingOutlined style={{ fontSize: '14px' }} spin />} />}
        </div>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width="85vw"
      style={{ top: 4 }}
      bodyStyle={{ padding: '12px 16px', maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' }}
      maskClosable={false}
      className="compact-product-dialog"
      footer={
        // 数据（产品详情/下拉选项/包装类型）加载完成前隐藏底部按钮，防止提前保存
        (loading || dataLoading || cascadeLoading) ? null : undefined
      }
    >
      <Form form={form} layout="vertical" className="compact-product-dialog" style={compactFormStyle}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          tabBarStyle={{ marginBottom: '12px' }}
        >
          {/* Tab 1: General Info */}
          <Tabs.TabPane tab="General Info" key="general">
            <Row gutter={[12, 8]}>
              {/* Basic Information */}
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Basic Information</Divider>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="Product Code"
                  name="productCode"
                  rules={[{ required: true, message: 'Please enter product code' }]}
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    size="small"
                    placeholder="Enter product code"
                    onChange={(e) => {
                      // Cust code follows Product Code while set to "Same" (table logic)
                      if (!customerProductCodeEnabled) {
                        form.setFieldsValue({ customerProductCode: e.target.value })
                      }
                    }}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="Customer's Product Code"
                  style={{ marginBottom: 8 }}
                >
                  <Space size="small">
                    <Switch
                      size="small"
                      checked={customerProductCodeEnabled}
                      onChange={handleCustomerProductCodeToggle}
                      disabled={mode === 'view'}
                    />
                    <Form.Item name="customerProductCode" noStyle>
                      <Input
                        size="small"
                        placeholder={customerProductCodeEnabled ? 'Enter customer code' : '(Same as Product Code)'}
                        disabled={!customerProductCodeEnabled}
                        style={{
                          width: 140,
                          fontStyle: customerProductCodeEnabled ? 'normal' : 'italic'
                        }}
                      />
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="Plain" name="plain" initialValue={0} style={{ marginBottom: 8 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    disabled={mode === 'view'}
                  >
                    <Option value={0}>No</Option>
                    <Option value={1}>Yes</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={6}>
                {/* Product source: derived from the base product (manufactured 1 = Outsourced, 0 = Manufactured) */}
                <Form.Item label="Product Source" style={{ marginBottom: 8 }}>
                  {productDetails?.baseProduct?.manufactured != null
                    ? (Number(productDetails.baseProduct.manufactured) === 1
                      ? <Tag color="orange">Outsourced</Tag>
                      : <Tag color="green">Manufactured</Tag>)
                    : <span style={{ color: '#bfbfbf' }}>-</span>}
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Product Name"
                  name="productName"
                  rules={[{ required: true, message: 'Please enter product name' }]}
                  style={{ marginBottom: 8 }}
                >
                  <Input size="small" placeholder="Enter product name" />
                </Form.Item>
              </Col>

              {/* Base Product: selectable; inherited specs/routings/pricing refresh on change */}
              <Col span={12}>
                <Form.Item
                  label={
                    <Space size={4}>
                      Base Product
                      <Tooltip title="Switching the base product refreshes the inherited specification details, routings and pricing (Specifications / Pricing tabs).">
                        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '13px' }} />
                      </Tooltip>
                    </Space>
                  }
                  name="baseProductId"
                  rules={[{ required: true, message: 'Please select a base product' }]}
                  style={{ marginBottom: 8 }}
                >
                  <Select
                    size="small"
                    placeholder="Search and select base product"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={mode === 'view'}
                    onChange={handleBaseProductChange}
                  >
                    {baseProductOptions.map((baseProduct: any) => (
                      <Option key={baseProduct.baseProductId} value={baseProduct.baseProductId}>
                        {`${baseProduct.baseProductName || ''}${baseProduct.productCode ? ` (${baseProduct.productCode})` : ''}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Stock & Order Information */}
              <Col span={24} className="spec-readonly">
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Stock & Order Information</Divider>
              </Col>

              <Col span={6} className="spec-readonly">
                <Form.Item
                  label={
                    <Space size="small">
                      Current Stock
                      <Popover content={stockInfoContent} title="Stock Details">
                        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '13px' }} />
                      </Popover>
                    </Space>
                  }
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    size="small"
                    value={
                      loading ? 'Loading...' : (
                        stockInfo?.productInventoryInfo
                          ? `${stockInfo.productInventoryInfo.productInStock} (${stockInfo.productInventoryInfo.productInStockCartons} Ctns)`
                          : '-'
                      )
                    }
                    readOnly
                  />
                </Form.Item>
              </Col>

              <Col span={6} className="spec-readonly">
                <Form.Item
                  label={
                    <Space size="small">
                      Customer Orders
                      <Popover content={stockInfoContent} title="Order Details">
                        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '13px' }} />
                      </Popover>
                    </Space>
                  }
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    size="small"
                    value={
                      loading ? 'Loading...' : (
                        stockInfo?.productInventoryInfo
                          ? `${stockInfo.productInventoryInfo.pendingOrderQuantity} (${stockInfo.productInventoryInfo.pendingOrderQuantityCartons} Ctns)`
                          : '-'
                      )
                    }
                    readOnly
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="Minimum Stock Level" name="productMsl" style={{ marginBottom: 8 }}>
                  <InputNumber
                    size="small"
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="Units"
                    disabled={mode === 'view'}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="MOQ for Order[Cartons]" name="minOrderQuantity" style={{ marginBottom: 8 }}>
                  <InputNumber
                    size="small"
                    min={0}
                    style={{ width: '100%' }}
                    placeholder="Cartons"
                    disabled={mode === 'view'}
                  />
                </Form.Item>
              </Col>

              {/* Image & Logo Settings */}
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Image & Logo Settings</Divider>
              </Col>

              <Col span={8}>
                <Form.Item label="Production Image" style={{ marginBottom: 8 }}>
                  <Upload {...buildUploadProps(setProductFileList, productFileList)}>
                    <Button size="small" icon={<UploadOutlined />}>Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="Display Image" style={{ marginBottom: 8 }}>
                  <Upload {...buildUploadProps(setCustomerImageFileList, customerImageFileList)}>
                    <Button size="small" icon={<UploadOutlined />}>Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label="Logo Settings" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Select
                      size="small"
                      placeholder="Select logo type"
                      style={{ width: '100%' }}
                      value={logoType}
                      options={logoTypeOptions}
                      onChange={setLogoType}
                    />
                    <Upload {...buildUploadProps(setLogoFileList, logoFileList)}>
                      <Button size="small" icon={<CameraOutlined />}>Upload Logo</Button>
                    </Upload>
                  </Space>
                </Form.Item>
              </Col>

            </Row>
          </Tabs.TabPane>

          {/* Tab 2: Specifications */}
          <Tabs.TabPane tab="Specifications" key="specifications">
            <Row className="compact-spec spec-readonly" gutter={[12, 4]}>
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Packaging & Palletisation</Divider>
              </Col>

              <Col span={6} className="spec-editable">
                <Form.Item
                  label="Pallet Type"
                  name="plateTypeId"
                  style={{ marginBottom: 8 }}
                  rules={[{ required: true, message: 'Please select pallet type' }]}
                >
                  <Select
                    size="small"
                    placeholder="Select pallet type"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={mode === 'view'}
                  >
                    {plateTypeOptions.map((plateType: any) => (
                      <Option key={plateType.plateTypeId} value={plateType.plateTypeId}>
                        {plateType.plateTypeName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={6} className="spec-editable">
                <Form.Item
                  label="Pallet Stacking"
                  name="palletStackingId"
                  style={{ marginBottom: 8 }}
                  rules={[{ required: true, message: 'Please select pallet stacking' }]}
                >
                  <Select
                    size="small"
                    placeholder="Select pallet stacking"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={mode === 'view'}
                  >
                    {palletStackingOptions.map((palletStacking: any) => (
                      <Option key={palletStacking.palletStackingId} value={palletStacking.palletStackingId}>
                        {`${palletStacking.palletStackingName} (${palletStacking.ctnsPerLevel ?? 0} ctns × ${palletStacking.level ?? 0} levels${palletStacking.addOnQty > 0 ? ` + ${palletStacking.addOnQty}` : ''})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Packaging Unit: stored on the BaseProduct; editable for 1:1 products */}
              <Col span={12} className={specEditable ? 'spec-editable' : 'spec-readonly'}>
                {specEditable ? (
                  <Form.Item name="packagingTypeId" noStyle>
                    <PackagingTypeCascadeSelect
                      inlinePreview
                      style={{ marginTop: 6 }}
                      onLoadingChange={setCascadeLoading}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Packaging Unit" style={{ marginBottom: 8 }}>
                    <Input
                      size="small"
                      value={loading ? 'Loading...' : (productDetails?.baseProduct?.packagingType?.packagingTypeName ?? '-')}
                      readOnly
                    />
                  </Form.Item>
                )}
              </Col>

              {/* Carton dimensions & gross weight: stored on the BaseProduct; editable for 1:1 products */}
              <Col span={6} className={specEditable ? 'spec-editable' : 'spec-readonly'}>
                <Form.Item label="Length (mm)" name="cartonLength" style={{ marginBottom: 8 }}>
                  <Input size="small" placeholder="mm" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={6} className={specEditable ? 'spec-editable' : 'spec-readonly'}>
                <Form.Item label="Width (mm)" name="cartonWidth" style={{ marginBottom: 8 }}>
                  <Input size="small" placeholder="mm" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={6} className={specEditable ? 'spec-editable' : 'spec-readonly'}>
                <Form.Item label="Height (mm)" name="cartonHeight" style={{ marginBottom: 8 }}>
                  <Input size="small" placeholder="mm" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={6} className={specEditable ? 'spec-editable' : 'spec-readonly'}>
                <Form.Item label="Gross Weight (kg)" name="grossWeigh" style={{ marginBottom: 8 }}>
                  <Input size="small" placeholder="kg" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              {/* Product Classification (3-level cascade, inherited from Base Product) */}
              <Col span={24} className={specEditable ? 'spec-editable' : undefined}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>
                  Product Classification
                </Divider>
                <Form.Item name="classificationId" hidden>
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Primary Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Select primary category"
                    value={classificationPrimaryId}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={!specEditable}
                    onChange={(value: any) => handleClassificationSelect('primary', value)}
                  >
                    {classificationChildrenOf(null).map((classification: any) => (
                      <Option key={classification.classificationId} value={classification.classificationId}>
                        {classification.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Child Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Select child category"
                    value={classificationChildId}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={!specEditable}
                    onChange={(value: any) => handleClassificationSelect('child', value)}
                  >
                    {classificationChildrenOf(classificationPrimaryId).map((classification: any) => (
                      <Option key={classification.classificationId} value={classification.classificationId}>
                        {classification.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Grandchild Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Select grandchild category"
                    value={classificationGrandchildId}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={!specEditable}
                    onChange={(value: any) => handleClassificationSelect('grandchild', value)}
                  >
                    {classificationChildrenOf(classificationChildId).map((classification: any) => (
                      <Option key={classification.classificationId} value={classification.classificationId}>
                        {classification.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Product Routing Options */}
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Product Routing</Divider>
              </Col>

              <Col span={24}>
                <Form.Item label="Routings" style={{ marginBottom: 8 }}>
                  {/* Routings are few (3-4) — cap the block height, scroll inside if expanded actions overflow */}
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {loading ? (
                      <Spin size="small" />
                    ) : productRoutings.length > 0 ? (
                      <Collapse activeKey={expandedRoutingKeys} onChange={handleRoutingCollapseChange}>
                        {productRoutings.map((routing: any) => {
                          const routingId = routing.id ?? routing.baseProductRoutingId
                          const routingName = routing.routingName || routing.productTypeName || `Routing ${routingId}`
                          return (
                            <Panel
                              key={routingId}
                              header={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontWeight: 500 }}>{routingName}</span>
                                  {routing.isDefault && (
                                    <Tag
                                      color="blue"
                                      icon={<StarFilled />}
                                      style={{ fontSize: '12px', lineHeight: '16px', marginInlineEnd: 0 }}
                                    >
                                      Default
                                    </Tag>
                                  )}
                                  {routing.isActive === false && (
                                    <Tag style={{ fontSize: '12px', lineHeight: '16px', marginInlineEnd: 0 }}>Inactive</Tag>
                                  )}
                                </div>
                              }
                            >
                              <Table
                                size="small"
                                columns={machineActionColumns}
                                dataSource={routingActions[routingId] || []}
                                loading={!!routingActionsLoading[routingId]}
                                pagination={false}
                                style={{ fontSize: '13px' }}
                                rowKey={(record: any) => record.id ?? record.baseProductActionRoutingId ?? `${record.actionId}-${record.machineId}`}
                              />
                            </Panel>
                          )
                        })}
                      </Collapse>
                    ) : (
                      <Text type="secondary">No routing configured</Text>
                    )}
                  </div>
                </Form.Item>
              </Col>

              <Col span={24} className={specEditable ? 'spec-editable' : undefined}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Specification Details</Divider>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Brim Fill Capacity (ml)" name="brimFillCapacityMl" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="ml" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              {sizePairItem('Top Size L x W (mm)', 'dimsTopLMm', 'dimsTopWMm')}
              {sizePairItem('Bottom Size L x W (mm)', 'dimsBaseLMm', 'dimsBaseWMm')}

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Height / Depth (mm)" name="productHeightMm" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Product Weight (g)" name="productWeightG" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="g" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Wall Thickness (mm)" name="wallThicknessMm" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Place of Production" name="manufacturedLocation" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="e.g. New Zealand" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Raw Material" name="rawMaterialText" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Material" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Printing Type" name="printingType" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Printing type" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={4} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Lining/Coating" name="liningCoating" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Type" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={8} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Factory Quality & Food Safety Certification" name="facilityQualitySystem" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="e.g. ISO 9001, HACCP, BRC" readOnly={!specEditable} />
                </Form.Item>
              </Col>

              <Col span={24} className={specEditable ? 'spec-editable' : undefined}>
                <Form.Item label="Environmental & Disposal" style={{ marginBottom: 2 }}>
                  <Space size="large" wrap>
                    <Form.Item name="isHomeCompostable" valuePropName="checked" noStyle>
                      <Checkbox disabled={!specEditable}>Home Compostable</Checkbox>
                    </Form.Item>
                    <Form.Item name="isIndustrialCompostable" valuePropName="checked" noStyle>
                      <Checkbox disabled={!specEditable}>Industrial Compostable</Checkbox>
                    </Form.Item>
                    <Form.Item name="isRecyclable" valuePropName="checked" noStyle>
                      <Checkbox disabled={!specEditable}>Recyclable</Checkbox>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Tabs.TabPane>

          {/* Tab 3: Pricing & Sales */}
          <Tabs.TabPane tab="Pricing & Sales" key="pricing">
            <Row gutter={[12, 8]}>
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Customer Scope</Divider>
              </Col>

              <Col span={24}>
                <Form.Item label="Customer Type" name="customerType" style={{ marginBottom: 8 }}>
                  <Radio.Group
                    size="small"
                    onChange={(e: any) => {
                      if (e.target.value === 'general') {
                        form.setFieldsValue({ customerId: null })
                      }
                    }}
                  >
                    <Radio value="specific">Customer Specific</Radio>
                    <Radio value="general">General</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.customerType !== cur.customerType}>
                  {({ getFieldValue }) => {
                    const isCustomerSpecific = getFieldValue('customerType') === 'specific'
                    return (
                      <Form.Item
                        label="Selected Customer"
                        name="customerId"
                        style={{ marginBottom: 8 }}
                        rules={isCustomerSpecific ? [{ required: true, message: 'Please select a customer' }] : []}
                      >
                        <Select
                          size="small"
                          placeholder="Search and select customer"
                          style={{ width: '100%' }}
                          showSearch
                          allowClear
                          disabled={!isCustomerSpecific}
                          optionFilterProp="children"
                        >
                          {customerOptions.map((customer: any) => (
                            <Option key={customer.customerId} value={customer.customerId}>
                              {`${customer.company || customer.customerName || customer.customerCode || customer.customerId}${customer.customerCode ? ` (${customer.customerCode})` : ''}`}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )
                  }}
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Base Product Pricing</Divider>
              </Col>

              <Col span={24}>
                <Table
                  size="small"
                  columns={basePricingColumns}
                  dataSource={loading ? [] : basePricingData}
                  pagination={false}
                  style={{ fontSize: '13px' }}
                  loading={loading}
                  rowKey={(record) => record.productPriceId}
                />
              </Col>

              <Col span={24}>
                <Divider orientation="left" style={{ margin: '8px 0', fontSize: '15px' }}>Customer Pricing & Sales</Divider>
              </Col>

              <Col span={24}>
                <Table
                  size="small"
                  columns={customerPricingColumns}
                  dataSource={loading ? [] : customerPricingData}
                  pagination={{
                    pageSize: 10,
                    size: 'small',
                    showSizeChanger: false,
                    hideOnSinglePage: true
                  }}
                  style={{ fontSize: '13px' }}
                  loading={loading}
                  showSorterTooltip={false}
                  rowKey={(record) => record.customerId}
                />
              </Col>
            </Row>
          </Tabs.TabPane>
        </Tabs>
      </Form>
    </Modal>
    <Modal
      visible={previewVisible}
      title="Image Preview"
      footer={null}
      onCancel={() => setPreviewVisible(false)}
      width={1000}
      style={{ top: 20 }}
    >
      <img
        alt="preview"
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '80vh',
          objectFit: 'contain',
          display: 'block'
        }}
        src={previewImage}
      />
    </Modal>
    </>
  )
}

export default ProductManagementDialog
